import { useMemo } from 'react'
import { useWatch } from 'react-hook-form'

import { useGetByTenantAccountQuery } from '@/api/rtk-api'
import { TENANT } from '@/const'
import { doesIncludeCaseInsensitive } from '@/utilities/regex'

import { BranchData } from '../tree'

export enum BRANCH_TYPE {
  client,
  business_unit,
  note
}

interface Item {
  amount: number
  id: string
  label: string
  type: BRANCH_TYPE
}

interface ItemWithSubBranch extends Item {
  childIds: string[]
  childIdsString: string
  subBranch: (Item | ItemWithSubBranch)[]
}

export const useSearchValue = () => {
  const searchValue = useWatch({ name: 'note-filters-search' })
  const isSearchActive = searchValue && searchValue.length > 1
  return { searchValue, isSearchActive }
}

export function getFilteredData(
  branch: Item | ItemWithSubBranch,
  searchValue: string
): BranchData | undefined {
  if (!branch) return undefined

  const doesBranchMatch = doesIncludeCaseInsensitive(branch.label, searchValue)
  if (doesBranchMatch) return branch

  if ('subBranch' in branch) {
    const subBranchMatches = Object.values(branch.subBranch)
      .map(branch => getFilteredData(branch, searchValue))
      .filter(branch => Boolean(branch))

    const doesSubBranchMatch = subBranchMatches.length > 0

    if (doesSubBranchMatch) {
      const branchClone = { ...branch } as unknown as BranchData
      branchClone.subBranch = subBranchMatches
      branchClone.childMatchesSearch = true
      return branchClone
    }
    return undefined
  }

  return undefined
}

export const useData = () => {
  const { data, isLoading } = useGetByTenantAccountQuery({
    tenant: TENANT
  })
  const { isSearchActive, searchValue } = useSearchValue()

  const transformedData = useMemo(() => {
    if (!data) return []
    return data.accounts.reduce<BranchData[]>((accumulator, account) => {
      const { businessUnit, client, noteId } = account

      const note = data.notes.find(note => note.noteId === noteId)
      const newNote = {
        label: note.noteName,
        id: noteId,
        type: BRANCH_TYPE.note,
        amount: 1
      }
      // We manually alter the Dominion Client ID here (and below when creating a newClient)
      // because the Dominion client and the Dominion note both have the same ID which is just the string "Dominion"
      const existingClient =
        client.clientId === 'Dominion'
          ? accumulator.find(c => c.id === 'DominionClient')
          : accumulator.find(c => c.id === client.clientId)
      const existingBusinessUnit = existingClient?.subBranch.find(
        bu => bu.id === businessUnit.businessUnitId
      ) as ItemWithSubBranch
      const existingNote = existingBusinessUnit?.subBranch.find(n => n.id === noteId)
      const newBusinessUnit = {
        type: BRANCH_TYPE.business_unit,
        id: businessUnit.businessUnitId,
        label: businessUnit.businessUnitName,
        amount: 1,
        childIds: [noteId],
        childIdsString: noteId,
        subBranch: [newNote]
      }
      const newClient = {
        label: client.clientName,
        type: BRANCH_TYPE.client,
        id: client.clientId === 'Dominion' ? 'DominionClient' : client.clientId,
        amount: 1,
        childIds: [businessUnit.businessUnitId],
        childIdsString: businessUnit.businessUnitId,
        subBranch: [newBusinessUnit]
      }

      if (!existingClient) {
        accumulator.push(newClient)
      } else if (!existingBusinessUnit) {
        existingClient.amount += 1
        existingClient.childIds.push(businessUnit.businessUnitId)
        existingClient.childIdsString = `${existingClient.childIdsString} ${businessUnit.businessUnitId}`
        existingClient.subBranch.push(newBusinessUnit)
      } else {
        existingClient.amount += 1
        existingBusinessUnit.amount += 1
        if (!existingNote) {
          existingBusinessUnit.childIds.push(noteId)
          existingBusinessUnit.childIdsString = `${existingBusinessUnit.childIdsString} ${noteId}`
          existingBusinessUnit.subBranch.push(newNote)
        } else {
          existingNote.amount += 1
        }
      }

      return accumulator
    }, [])
  }, [data])

  const filteredData = isSearchActive
    ? transformedData
        .map(branch => getFilteredData(branch, searchValue))
        .filter(branch => Boolean(branch))
    : transformedData

  return {
    data: filteredData,
    isLoading
  }
}
